/* tslint:disable:radix */
import { KeyValue } from '@angular/common'
import { inject, NgZone } from '@angular/core'
import camelCase from 'lodash-es/camelCase'
import isEqual from 'lodash-es/isEqual'
import merge from 'lodash-es/merge'
import { MegaMenuItem } from 'primeng/api'
import { Cry } from './cry.service'
import { GlobalService } from './global.service'

/**
 * Function that simplifies service injection outside the constructor
 * @param token
 * @returns {T}
 */
export const use = <T>(token): T => {
  try {
    return inject<T>(token)
  } catch (e) {
    // console.info('**', token)
    return window['ctorinje'].get(token)
  }
}

export const chunkArray = (array, chunk) => {
  let tempArray = []
  for (let i = 0; i < array.length; i += chunk) tempArray = [...tempArray, array.slice(i, i + chunk)]
  return tempArray
}

export const pad = (str, max, fill?) => {
  str = str.toString()
  return str.length < max ? pad(fill || "0" + str, max, fill) : str
}

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const pascalCase = (string: string) => {
  const str = camelCase(string)
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// Escapes a value for safe interpolation into HTML strings (emails, stored rich-text history entries)
export const escapeHtml = (value: any): string => {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * Converts a flat array of categories into a hierarchical menu structure.
 * Uses IDs to establish parent-child relationships via parentId.
 *
 * @param categories Array of categories to be transformed
 * @param parentValue Value of the parentId field to identify root nodes
 * @param parentField Name of the field containing the parent reference. Default: 'parentId'
 * @returns Array of menu items formatted as MegaMenuItem[]
 */
export const treeify = (categories: any[], parentValue: any = null, parentField: string = 'parentId'): MegaMenuItem[] => {
  // Build children map by parentId
  const childrenMap = new Map<string, any[]>();

  for (const category of categories) {
    const pid = category[parentField];
    if (pid) {
      if (!childrenMap.has(pid)) {
        childrenMap.set(pid, []);
      }
      childrenMap.get(pid)!.push(category);
    }
  }

  // Recursive function to build subitems (grandchildren onwards)
  const buildSubItems = (parentId: string): any[] => {
    const children = childrenMap.get(parentId) || [];
    return children.map(child => {
      const subItems = buildSubItems(child.id);
      if (subItems.length > 0) {
        return {
          label: child.name,
          items: subItems
        };
      }
      return {
        label: child.name,
        routerLink: `/rewards/products/subcategory/${child.id}`
      };
    });
  };

  // Identify root nodes (categories whose parentId === parentValue)
  const rootElements = categories.filter(cat => cat[parentField] === parentValue);

  // Build the hierarchical menu structure
  const result: MegaMenuItem[] = rootElements.map(rootElem => {
    // Direct children of root (will be MegaMenu columns)
    const children = childrenMap.get(rootElem.id) || [];

    // Each child is a separate column in the MegaMenu
    const columns = children.map(child => {
      const subItems = buildSubItems(child.id);
      return [{
        label: child.name,
        items: subItems.length > 0 ? subItems : undefined
      }];
    });

    return {
      label: rootElem.name,
      items: columns.length > 0 ? columns : undefined
    };
  });

  return result;
}

/**
 * Version of treeify that returns the children of parentId as root items of MegaMenu.
 * Useful for multi-menus where we want to show the children of a macro menu as main items.
 *
 * PrimeNG MegaMenu structure (limited to 2 levels in dropdown):
 * - Root level (MegaMenuItem[]): items is MenuItem[][] (columns)
 * - Inside columns (MenuItem[]): items is MenuItem[] (subitems - only links, no more nesting)
 *
 * Levels 3+ are flattened to direct links.
 */
export const treeifyFromParent = (categories: any[], parentId: string): MegaMenuItem[] => {
  const childrenMap = new Map<string, any[]>();

  for (const category of categories) {
    const pid = category.parentId;
    if (pid) {
      if (!childrenMap.has(pid)) {
        childrenMap.set(pid, []);
      }
      childrenMap.get(pid)!.push(category);
    }
  }

  // Builds subitems as direct links (without additional nesting)
  // PrimeNG MegaMenu only supports 2 levels in dropdown
  const buildFlatItems = (pid: string): any[] => {
    const children = childrenMap.get(pid) || [];
    return children.map(child => ({
      label: child.name,
      routerLink: `/rewards/products/subcategory/${child.id}`
    }));
  };

  // Direct children of parentId will be the root items (level 1)
  const level1Children = childrenMap.get(parentId) || [];

  return level1Children.map(level1 => {
    // Children of level 1 will be the columns (level 2)
    const level2Children = childrenMap.get(level1.id) || [];

    // Each column is a MenuItem[][] where each sub-array is a column
    const columns: any[][] = level2Children.map(level2 => {
      const subItems = buildFlatItems(level2.id);
      return [{
        label: level2.name,
        items: subItems.length > 0 ? subItems : undefined
      }];
    });

    return {
      label: level1.name,
      items: columns.length > 0 ? columns : undefined
    };
  });
}

export const orientation = () => {
  if (window.matchMedia("(orientation: landscape)").matches) {
    return 'landscape'
  } else if (window.matchMedia("(orientation: portrait)")) {
    return 'portrait'
  }
}

export const isEmpty = (obj) => {
  for (const key in obj) {
    if (obj[key])
      return false
  }
  return true
}

/**
 * Removes attributes started by a predefined string in a simple object
 * @param obj The object to have attributes removed from
 * @param startedBy The initial string that defines the attribute to be removed
 *
 * Usage example:
 * const obj = { name: 'walter', age: 25, _checked: true };
 * const newObj = clearFieldsStartedBy(obj, '_');
 * This removes the _checked attribute from the object.
 *
 * Use cases:
 * - Remove temporary attributes used for displaying transformed data in the template
 */
export const clearFieldsStartedBy = (obj: any, startedBy: string): any => {
  if (obj instanceof Array) {
    obj = obj.map(o => clearFieldsStartedBy(o, startedBy))
    return obj
  }
  const tmp = {}
  for (const key in obj) if (!key.startsWith(startedBy)) tmp[key] = obj[key]
  return tmp
}

/**
 * Removes attributes with empty or null values in a simple object
 * @param obj The object to have attributes removed from
 * @param removeNull TRUE if you also want to remove NULL values
 *
 * Usage example:
 * const obj = { name: 'walter', age: 25, checked: '' };
 * const newObj = clearFieldsBlank(obj);
 * This removes the checked attribute from the object.
 *
 * Use cases:
 * - Remove temporary attributes used for displaying transformed data in the template
 */
export const clearFieldsBlank = (obj: any, removeNull: boolean = false): any => {
  if (obj instanceof Array) {
    obj = obj.map(o => clearFieldsBlank(o, removeNull))
    return obj
  }
  const tmp = {}
  for (const key in obj) if (obj[key] !== '') tmp[key] = obj[key]
  for (const key in obj) if (obj[key] === null && removeNull) delete tmp[key]
  return tmp
}

export function selectSegmentsAndSubSegments(segmentId: string, g: any, selecteds = new Set<string>()): Set<string> {
  if (!selecteds.has(segmentId)) selecteds.add(segmentId)
  const found = (g.get('segments') || []).filter(s => s.parentId === segmentId)
  if (found.length) {
    for (const f of found) {
      selecteds = selectSegmentsAndSubSegments(f.id, g, selecteds)
    }
  }
  return selecteds
}

/**
 * Filters a collection of items by the segments of the currently logged-in user
 * For this function to work correctly, the collections to be filtered must contain the following two attributes in their items:
 *  includedSegments: string[] - An array with the IDs of segments that CAN access the item
 *  excludedSegments: string[] - An array with the IDs of segments that CANNOT access the item
 * @param {any[]} items
 * @param {GlobalService} g
 * @returns {any[]}
 */
export function filterBySegments(items: any[], g: GlobalService): any[] {
  const showTo = [...(g.user?.segment ? [g.user.segment.id] : []), ...(g.user?.segments || [])]
  // console.log('#show', showTo)
  const forAll = items.filter(i => (!i.includedSegments || !i.includedSegments.length) && (!i.excludedSegments || !i.excludedSegments.length))
  const restriced = items.filter(i => (i.includedSegments && i.includedSegments.length) || (i.excludedSegments && i.excludedSegments.length))
    .map(i => ({
      ...i,
      _showTo: (i.includedSegments || []).map(s => selectSegmentsAndSubSegments(s, g)).reduce((a, b) => [...a, ...b], []),
      _hideTo: (i.excludedSegments || []).map(s => selectSegmentsAndSubSegments(s, g)).reduce((a, b) => [...a, ...b], [])
    }))
    .map(i => ({ ...i, _showTo: (!i.hasOwnProperty('showToChildren') || i.showToChildren) ? i._showTo : (i.includedSegments || []) }))
    .filter(i => i._hideTo.filter(h => showTo.includes(h)).length === 0 && i._showTo.filter(s => showTo.includes(s)).length > 0)
  // console.log('#resticed', restriced.map(({headline, _hideTo, _showTo}) => ({headline, _hideTo, _showTo})))
  return [...forAll, ...clearFieldsStartedBy(restriced, '.')]
}

/* discontinued, delete soon */
export function filterBySegments2(items: any[], user: any) {
  console.log(items[0])
  return items
    .filter(p => !p.segment || !p.segment.length || p.segment.some(s => (user.segments || []).indexOf(s) > -1 || user.segment.id === s))
    .filter(p => !p.excludeSegments || !p.excludeSegments.length || p.excludeSegments.some(s => (user.segments || []).indexOf(s) === -1 && user.segment.id !== s))
}

/*
  *
  * @function constructObject
  * @param {file} file in dot notation ('a.b.c':'Hey', 'd.e.g': 'you')
  * @return {Object} - a nested object.
  * @example
  * const object = constructObject(file)
  * Returns the following:
  * a: {
  *   b: {
  *     c: 'hey'
  *   }
  * }
  * d: {
  *   e: {
  *     g: 'you'
  *   }
  * }
*/
export function objectToArray(obj, idName?, valueName?): any {
  const array = []
  for (const prop in obj) {
    if (obj[prop]) {
      const newObj = {}
      newObj[idName || 'id'] = prop
      newObj[valueName || 'value'] = obj[prop]
      array.push(newObj)
    }
  }
  return array
}

export const arrayToObject = (array, key, field?) => {
  return array.reduce((obj, item) => {
    obj[item[key]] = field ? item[field] : item
    return obj
  }, {})
}

// Get the nested object/key without the value
const getKey = (arr) => {
  const nestedObject = {}
  arr.reduce((o, s, index) => {
    if (index + 1 < arr.length) {
      return o[s] = {}
    }
  }, nestedObject) // eslint-disable-line
  return nestedObject
}

// Get the full object here
const getNested = (arr, val) => {
  const nestedObject = {}
  arr.reduce((o, s, index) => {
    if (index + 1 === arr.length) {
      return o[s] = val
    } else {
      return o[s] = {}
    }
  }, nestedObject) // eslint-disable-line
  return nestedObject
}

export const getNestedObject = (file) => {
  let final = {}
  const parsed = JSON.parse(JSON.stringify(file))
  Object.keys(parsed).forEach((key) => {
    const keysArr = key.split('.')
    if (keysArr.length > 1) {
      final = merge(final, getKey(keysArr), getNested(keysArr, parsed[key]))
    } else { final[keysArr[0]] = parsed[key] }
  })
  return final
}

export const excelDateToISOdate = (date) => {
  const d = new Date(Math.round((date - 25569) * 86400 * 1000))
  return d.toISOString()
}

// export default constructObject
export const originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
  return 0
}

// Order by ascending property value
export const valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
  return a.value.localeCompare(b.value)
}

// Order by descending property key
export const keyDescOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
  return a.key > b.key ? -1 : (b.key > a.key ? 1 : 0)
}

export const filterObject = (obj, filter, filterValue) =>
  Object.keys(obj).reduce((acc, val) => (obj[val][filter] === filterValue ? { ...acc, [val]: obj[val] } : acc), {})

export const formatDate = (date) => {
  const d = new Date(date),
    year = d.getFullYear()
  let month = '' + (d.getMonth() + 1),
    day = '' + d.getDate()

  if (month.length < 2)
    month = '0' + month
  if (day.length < 2)
    day = '0' + day

  return [year, month, day].join('-')
}

/** setTimeout using NgZone, better for performance **/
export const timeOut = (fn: Function, ms: number = 10, zone = new NgZone({ enableLongStackTrace: false })) => zone.runOutsideAngular(() => setTimeout(() => zone.run(() => fn()), ms))

/*
 * Compare two objects by reducing an array of keys in obj1, having the
 * keys in obj2 as the intial value of the result. Key points:
 *
 * - All keys of obj2 are initially in the result.
 *
 * - If the loop finds a key (from obj1, remember) not in obj2, it adds
 *   it to the result.
 *
 * - If the loop finds a key that are both in obj1 and obj2, it compares
 *   the value. If it's the same value, the key is removed from the result.
 */
export const getObjectDiff = (obj1, obj2) => {
  return Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key)
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key)
      result.splice(resultKeyIndex, 1)
    }
    return result
  }, Object.keys(obj2))
}

export const sleep = ms => {
  return new Promise(resolve => {
    timeOut(resolve, ms)
  })
}

export const deepFreeze = o => {
  Object.freeze(o)

  Object.getOwnPropertyNames(o).forEach(prop => {
    if (o.hasOwnProperty(prop)
      && o[prop] !== null
      && (typeof o[prop] === "object" || typeof o[prop] === "function")
      && !Object.isFrozen(o[prop])) {
      deepFreeze(o[prop])
    }
  })

  return o
}

/**
 * Removes attributes started by a predefined string in a complex object
 * @param obj The object to have attributes removed from
 * @param startedBy The initial string that defines the attribute to be removed
 *
 * Usage example:
 * const obj = { name: 'walter', age: 25, _checked: true };
 * const newObj = clearFieldsStartedBy(obj, '_');
 * This removes the _checked attribute from the object.
 *
 * Use cases:
 * - Remove temporary attributes used for displaying transformed data in the template
 */
export const clearFieldsStartedByNested = (obj: any, startedBy: string): any => {
  let temp

  if (obj instanceof Array) {
    temp = []
    for (const item of obj) {
      temp.push(clearFieldsStartedByNested(item, startedBy))
    }
  } else {
    temp = {}
    for (const key in obj) {
      if (!key.startsWith(startedBy)) {
        if (typeof obj[key] === 'object') temp[key] = clearFieldsStartedByNested(obj[key], startedBy)
        else temp[key] = obj[key]
      }
    }
  }

  return temp
}

export const sumBy = (arr: any[], prop: string) => arr.map(i => i[prop]).reduce((acc, val) => acc + val, 0)

export const rndBetween = (from: number, to: number) => {
  const nativeArr = new Uint8Array(1)
  const crypto = window['crypto'] || window['msCrypto']
  crypto.getRandomValues(nativeArr)
  const num = [].slice.call(nativeArr)[0]
  return (num >= from && num <= to) ? num : rndBetween(from, to)
}

/**
 * Recursive function that transforms a list of items interrelated by a PARENT parameter into an array compatible with PrimeNG TreeTable
 * @param items Array with the listing
 * @param raw A copy of the array with the listing
 * @param sortBy Name of the field to be sorted. Default: 'order'
 * @param first TRUE if it is the first call of the method
 * @param expand TRUE if the tree should remain open
 *
 * @return TreeNode[] Standard array for use with TreeTable
 *
 * Usage example:
 * this.parsedMenus = parseTableTree(menus, [...menus], 'name', true)
 */
export const parseTableTree = (items: any[], raw: any[], sortBy: string = 'order', first = false, expand = true, parentField = 'parentId') => {
  const items_ = first ? items.filter(m => !m[parentField]).sort((a, b) => a[sortBy] === b[sortBy] ? 0 : a[sortBy] < b[sortBy] ? -1 : 1) : items
  return items_.map((m: any) => {
    const node = { data: { ...m }, label: m.label || m.name, children: [], expanded: expand }
    node.children = parseTableTree(raw.filter(i => i[parentField] === m.id).sort((a, b) => a[sortBy] === b[sortBy] ? 0 : a[sortBy] < b[sortBy] ? -1 : 1), raw, sortBy, false, expand)
    return node
  })
}

/**
 * Recursive function that transforms a list of items interrelated by a PARENT parameter into an array compatible with PrimeNG TreeMenu
 * @param items Array with the listing
 * @param raw A copy of the array with the listing
 * @param firstParent String with ID of the topmost menu (program type)
 * @param sortBy Name of the field to be sorted. Default: 'order'
 *
 * @return TreeNode[] Standard array for use with TreeMenu
 *
 * Usage example:
 * this.parsedMenus = parseMenuTree(menus, [...menus], 'xyzxyz', 'name')
 */
export const parseMenuTree = (items: any[], raw: any[], firstParent: string = null, sortBy: string = 'order') => {
  const items_ = firstParent !== null ? items.filter(m => m.parentId === firstParent).sort((a, b) => a[sortBy].toLowerCase() === b[sortBy].toLowerCase() ? 0 : a[sortBy].toLowerCase() < b[sortBy].toLowerCase() ? -1 : 1) : items
  return items_.map((m: any) => {
    const node = { label: m.name, data: m }
    if (m.route) node['routerLink'] = m.route // [m.route];
    if (m.icon) node['icon'] = m.icon
    if (raw.filter(i => i.parentId === m.id).length) {
      node['items'] = parseMenuTree(raw.filter(i => i.parentId === m.id).sort((a, b) => a[sortBy].toLowerCase() === b[sortBy].toLowerCase() ? 0 : a[sortBy].toLowerCase() < b[sortBy].toLowerCase() ? -1 : 1), raw, null, sortBy)
    }
    return node
  })
}

export const adjustSizes = () => {
  const display = { w: window.innerWidth, h: window.innerHeight, r: window.innerWidth / window.innerHeight }
  if (window.innerWidth > window.innerHeight) display.w = window.innerWidth; display.h = window.innerHeight; display.r = window.innerWidth / window.innerHeight
  // console.log(display)
  const content = { w: 1920, h: 1080, r: 16 / 9 }
  const view = { w: 0, h: 0, r: 0, mgX: 0, mgY: 0, mgR: 0, mgL: 0, fontSize: 16, ratio: '' }
  view.w = content.r > display.r ? display.w : display.w / display.r * content.r
  view.h = content.r > display.r ? display.w / content.r : display.h
  view.mgX = (display.w - view.w) / 2
  view.mgY = (display.h - view.h) / 2
  view.mgR = view.mgX * 2 > view.h / 5 ? view.h / 5 : view.mgX * 2
  view.mgL = view.mgX * 2 - view.mgR
  view.fontSize = display.r < content.r ? display.h / content.h * 16 : display.w / content.w * 16
  const el = document.documentElement
  for (const [key, value] of Object.entries(view)) {
    // console.log(`${key}: ${value}`)
    el.style.setProperty(`--${key}`, `${value}px`)
  }
  // view.ratio = (content.w / content.h).toString()
  // el.style.setProperty(`--ratio`, (content.w / content.h).toString())
  // this.g.set('view', view)
  return view
  // console.log(view)
}

export const validateNIF = nif => {
  // transforms the nif into String in case it comes as Number
  nif = `${nif}`
  // Remove spaces and other non-numeric characters
  nif = nif.replace(/[^0-9]/g, '')

  // Verify if the NIF has the correct size (9 digits)
  if (nif.length !== 9) {
    return false
  }

  // Calculate the check digit
  let soma = 0
  for (let i = 0; i < 8; i++) {
    soma += parseInt(nif[i]) * (9 - i)
  }

  const resto = soma % 11
  const digitoControlo = (11 - resto) % 11

  // Verify if the calculated check digit matches the last digit of the NIF
  return digitoControlo === parseInt(nif[8])
}

/**
 * Shuffles an array
 * @param {any[]} items
 * @returns {any[]}
 */
export const shuffle = (items: any[]) => {
  let currentIndex = items.length
  let tempValue
  let randIndex

  while (0 !== currentIndex) {
    randIndex = rndBetween(0, items.length - 1)
    currentIndex -= 1

    tempValue = items[currentIndex]
    items[currentIndex] = items[randIndex]
    items[randIndex] = tempValue
  }

  return items
}

export const estimatedReadingTime = (text) => {
  if (text) {
    const wordsPerMinute = 200
    const words = text.split(' ').length
    return Math.ceil(words / wordsPerMinute)
  }
  return 0
}

export const verifyLoggedIn = (g, router, auth?) => {
  // test for SSR
  if (g.isBrowser) {
    timeOut(() => {
      const path = window.location.pathname
      // Keep the query string so deep-links (e.g. approval tokens) survive the login redirect
      const fullUrl = path + (window.location.search || '')
      const host = window.location.host || window.location.hostname
      if (path.indexOf('login/token') === -1 && path.indexOf('app-token') === -1 && path.indexOf('/pub/') === -1 && path.indexOf('/p/') === -1 && path.indexOf('login/signup-hash') === -1 && path.indexOf('login/resetPassword') === -1) {
        if (!g.user && !g.program.logins.anonymous) {
          if (path.indexOf('login') === -1 && path !== '/') {
            g.set('redirect', fullUrl, true)
          }
          router.navigate(['/login'])
          return
        }

        // Redirect to admin for centralized backoffice domain (admin.) - requires admin role
        if (g.isAdminDomain && g.user && g.user.role === 'admin') {
          router.navigate(['/admin'])
          return
        }
        // Non-admin users on admin. domain are not allowed - redirect to login
        if (g.isAdminDomain && g.user && g.user.role !== 'admin') {
          router.navigate(['/login'])
          return
        }
        // Redirect to admin for .bo. domain - requires admin or manager role
        if (path === '/' && g.isBoDomain && g.user && ['admin', 'manager'].includes(g.user.role)) {
          router.navigate(['/admin'])
          return
        }
        if (path === '/' && g.isBoDomain && !g.user) {
          router.navigate(['/login'])
          return
        }
        if ((g.user && g.user.uid && g.user.active) /*|| g.program.logins.anonymous*/) {
          if (path === '/' && auth?.redirectUser) auth.redirectUser()
        }
      }
    }, 250)
  }
}

export const getLangPrefix = lang => lang.split('-')[0] === 'pt' ? '' : lang.split('-')[0]

/**
 *
 *  Base64 encode / decode
 *  http://www.webtoolkit.info
 *
 **/
export const Base64 = {

  // private property
  _keyStr: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",

  // public method for encoding
  encode: function (input) {
    let output = ""
    let chr1, chr2, chr3, enc1, enc2, enc3, enc4
    let i = 0

    input = Base64._utf8_encode(input)

    while (i < input.length) {
      chr1 = input.charCodeAt(i++)
      chr2 = input.charCodeAt(i++)
      chr3 = input.charCodeAt(i++)

      // tslint:disable-next-line:no-bitwise
      enc1 = chr1 >> 2
      // tslint:disable-next-line:no-bitwise
      enc2 = ((chr1 & 3) << 4) | (chr2 >> 4)
      // tslint:disable-next-line:no-bitwise
      enc3 = ((chr2 & 15) << 2) | (chr3 >> 6)
      // tslint:disable-next-line:no-bitwise
      enc4 = chr3 & 63

      if (isNaN(chr2)) {
        enc3 = enc4 = 64
      } else if (isNaN(chr3)) {
        enc4 = 64
      }

      output = output +
        this._keyStr.charAt(enc1) + this._keyStr.charAt(enc2) +
        this._keyStr.charAt(enc3) + this._keyStr.charAt(enc4)
    } // Whend

    return output
  }, // End Function encode


  // public method for decoding
  decode: function (input) {
    let output = ""
    let chr1, chr2, chr3
    let enc1, enc2, enc3, enc4
    let i = 0

    input = input.replace(/[^A-Za-z0-9\+\/\=]/g, "")
    while (i < input.length) {
      enc1 = this._keyStr.indexOf(input.charAt(i++))
      enc2 = this._keyStr.indexOf(input.charAt(i++))
      enc3 = this._keyStr.indexOf(input.charAt(i++))
      enc4 = this._keyStr.indexOf(input.charAt(i++))

      // tslint:disable-next-line:no-bitwise
      chr1 = (enc1 << 2) | (enc2 >> 4)
      // tslint:disable-next-line:no-bitwise
      chr2 = ((enc2 & 15) << 4) | (enc3 >> 2)
      // tslint:disable-next-line:no-bitwise
      chr3 = ((enc3 & 3) << 6) | enc4

      output = output + String.fromCharCode(chr1)

      if (enc3 !== 64) {
        output = output + String.fromCharCode(chr2)
      }

      if (enc4 !== 64) {
        output = output + String.fromCharCode(chr3)
      }

    } // Whend

    output = Base64._utf8_decode(output)

    return output
  }, // End Function decode


  // private method for UTF-8 encoding
  _utf8_encode: function (string) {
    let utftext = ""
    string = string.replace(/\r\n/g, "\n")

    for (let n = 0; n < string.length; n++) {
      const c = string.charCodeAt(n)

      if (c < 128) {
        utftext += String.fromCharCode(c)
      } else if ((c > 127) && (c < 2048)) {
        // tslint:disable-next-line:no-bitwise
        utftext += String.fromCharCode((c >> 6) | 192)
        // tslint:disable-next-line:no-bitwise
        utftext += String.fromCharCode((c & 63) | 128)
      } else {
        // tslint:disable-next-line:no-bitwise
        utftext += String.fromCharCode((c >> 12) | 224)
        // tslint:disable-next-line:no-bitwise
        utftext += String.fromCharCode(((c >> 6) & 63) | 128)
        // tslint:disable-next-line:no-bitwise
        utftext += String.fromCharCode((c & 63) | 128)
      }

    } // Next n

    return utftext
  }, // End Function _utf8_encode

  // private method for UTF-8 decoding
  _utf8_decode: function (utftext) {
    let string = ""
    let i = 0
    let c, c1, c2, c3
    c = c1 = c2 = 0

    while (i < utftext.length) {
      c = utftext.charCodeAt(i)

      if (c < 128) {
        string += String.fromCharCode(c)
        i++
      } else if ((c > 191) && (c < 224)) {
        c2 = utftext.charCodeAt(i + 1)
        // tslint:disable-next-line:no-bitwise
        string += String.fromCharCode(((c & 31) << 6) | (c2 & 63))
        i += 2
      } else {
        c2 = utftext.charCodeAt(i + 1)
        c3 = utftext.charCodeAt(i + 2)
        // tslint:disable-next-line:no-bitwise
        string += String.fromCharCode(((c & 15) << 12) | ((c2 & 63) << 6) | (c3 & 63))
        i += 3
      }

    } // Whend

    return string
  } // End Function _utf8_decode

}


export const isColorDark = (hexColor: string) => {
  const color = hexColor.replace("#", "")
  // console.log('HexColor', color)

  // Extract the values of the RGB color channels
  const r = parseInt(color.slice(0, 2), 16)
  const g = parseInt(color.slice(2, 4), 16)
  const b = parseInt(color.slice(4, 6), 16)

  // Calculate the luminance value of the color using the W3C formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Verify if the color is considered dark or light based on the luminance value
  return luminance <= 0.6
}

/**
 * Transforms a flat array of objects with a parent reference into a TreeNode tree structure.
 *
 * @param {Array} flatArray - The flat array of objects.
 * @param {string} parentIdField - The name of the field containing the parent ID. Default is 'parentId'.
 * @param {*} rootParentValue - The value of parentIdField that identifies root nodes (e.g.: null, undefined, 0). Default is null.
 * @returns {TreeNode[]} - An array of root nodes of the TreeNode tree.
 */
export function buildTree(flatArray, parentIdField = 'parentId', rootParentValue = null) {
  if (!Array.isArray(flatArray)) {
    console.error("O argumento 'flatArray' deve ser um array.");
    return [];
  }

  const fieldName = parentIdField; // Name of the field to be used as parent reference

  // Maps all items by ID for quick access
  const map = new Map();
  flatArray.forEach(item => {
    // Creates a new default TreeNode object, copying known properties
    // and ensuring required fields like 'key'.
    const treeNode = {
      // Required or common fields
      key: item.id?.toString(), // Converts the ID to string, required by PrimeNG
      label: item.label || item.name || item.title, // Tries to map the label

      // Optional fields that may come from your original object
      data: item.data || item, // The original object can be used as data or a subset
      icon: item.icon,
      expandedIcon: item.expandedIcon,
      collapsedIcon: item.collapsedIcon,
      leaf: item.leaf, // Can be defined by the user
      expanded: item.expanded,
      type: item.type,
      partialSelected: item.partialSelected,
      style: item.style,
      styleClass: item.styleClass,
      draggable: item.draggable,
      droppable: item.droppable,
      selectable: item.selectable,
      checked: item.checked, // Added according to provided interface
      loading: item.loading, // Added according to provided interface

      // Field that will be populated by this function
      children: []
    };

    // Remove potential undefined values to keep it clean
    Object.keys(treeNode).forEach(key => {
      if (treeNode[key] === undefined) {
        delete treeNode[key];
      }
    });

    map.set(item.id, treeNode);
  });

  // Array to store the root nodes
  const roots = [];

  // Iterate again to build parent-child relationships
  flatArray.forEach(item => {
    const node = map.get(item.id);
    if (node) { // Verify if the node was created correctly
      const parentId = item[fieldName]; // Uses the field name dynamically
      const isRootByNullParent = parentId == null && rootParentValue == null;
      const isRootBySpecificParent = rootParentValue != null && parentId === rootParentValue;
      const isOrphan = rootParentValue == null && !map.has(parentId);
      if (isRootByNullParent || isRootBySpecificParent || isOrphan) {
        // If it has no parent or the parent is not in the list, it is a root node
        roots.push(node);
      } else {
        // Otherwise, find the parent and add this node as a child
        const parentNode = map.get(parentId);
        if (parentNode) {
          parentNode.children.push(node);
          // Mark the parent explicitly as non-leaf if it now has children
          // Unless the user has already defined `leaf: true` explicitly
          if (parentNode.leaf === undefined) {
            parentNode.leaf = false;
          }
        }
      }
    }
  });

  return roots;
}


/**
 * Unwraps the new API `ApiResult` envelope, returning its `result` payload.
 *
 * Throws unless the response is an envelope with `success === true`. This
 * covers `{ success: false }` and the `HttpErrorResponse` that `ApiService.post`
 * resolves with on HTTP errors (it has no `success` field), so callers never
 * mistake an error object for a valid payload. Use this instead of
 * `res?.result ?? res` fallbacks.
 */
export function unwrapApiResult<T = any>(res: any): T {
  if (res?.success === true) return res.result
  const error = res?.error
  const envelopeError = res?.success === false && (typeof error === 'string' ? error : error?.message || error?.code)
  throw new Error(envelopeError || res?.message || 'API request failed')
}
